import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.chart = echarts.init(this.element.querySelector('#org-design-cash-flow-chart'));
    const names = JSON.parse(this.data.get('org_names'));
    const rates = JSON.parse(this.data.get('org_rate'));
    const money = JSON.parse(this.data.get('org_endmoney'));
    const number = value => value == null || !Number.isFinite(Number(value)) ? null : Number(value);
    this.rows = names.map((name, index) => ({ name, rate: number(rates[index]), money: number(money[index]) }))
      .sort((a, b) => (a.rate ?? Infinity) - (b.rate ?? Infinity));
    this.draw();
    this.observer = new ResizeObserver(() => this.layout());
    this.observer.observe(this.element);
    this.themeObserver = new MutationObserver(() => this.draw());
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-coreui-theme', 'data-bs-theme'] });
  }

  draw() {
    const text = getComputedStyle(this.element).color;
    const format = value => value == null ? '暂无数据' : value.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
    this.chart.setOption({
      textStyle: { color: text },
      aria: { enabled: true },
      grid: { left: this.element.clientWidth < 600 ? 120 : 210, right: 85, top: 45, bottom: 45 },
      tooltip: {
        trigger: 'axis',
        renderMode: 'richText',
        confine: true,
        axisPointer: { type: 'shadow' },
        formatter: params => {
          const row = this.rows[params[0].dataIndex];
          return `${row.name}\n折算月份：${format(row.rate)}${row.rate == null ? '' : ' 月'}\n现金流：${format(row.money)} 万元`;
        }
      },
      toolbox: { feature: { saveAsImage: { title: '保存图片' } }, iconStyle: { borderColor: text } },
      xAxis: {
        type: 'value', name: '折算月份（月）', nameLocation: 'middle', nameGap: 30,
        min: extent => extent.min < 0 ? extent.min - (extent.max - extent.min || 1) * 0.12 : 0,
        max: extent => extent.max > 0 ? extent.max + (extent.max - extent.min || 1) * 0.12 : 0,
        axisLabel: { color: text, formatter: value => value.toLocaleString('zh-CN', { maximumFractionDigits: 1 }), hideOverlap: true },
        splitLine: { lineStyle: { color: text, opacity: 0.12 } }
      },
      yAxis: {
        type: 'category', inverse: true, data: this.rows.map(row => row.name),
        axisLabel: { color: text, width: this.element.clientWidth < 600 ? 100 : 190, overflow: 'truncate', interval: 0 },
        axisTick: { show: false }, axisLine: { show: false }
      },
      dataZoom: [{
        type: 'slider', yAxisIndex: 0, right: 0, width: 18,
        show: this.rows.length > 15, startValue: 0, endValue: 14,
        filterMode: 'filter', zoomLock: true, showDetail: false,
        brushSelect: false, textStyle: { color: text }
      }],
      series: [{
        name: '折算月份', type: 'bar', barMaxWidth: 18,
        data: this.rows.map(row => ({
          value: row.rate,
          itemStyle: { color: row.rate < 0 ? '#e66b75' : '#36b5a5', borderRadius: 3 },
          label: { position: row.rate < 0 ? 'left' : 'right' }
        })),
        label: { show: true, color: text, formatter: params => format(params.value), fontSize: 12 },
        markLine: { silent: true, symbol: 'none', label: { show: false }, lineStyle: { color: text, opacity: 0.5, type: 'solid' }, data: [{ xAxis: 0 }] }
      }]
    });
  }

  layout() {
    this.chart.resize();
  }

  disconnect() {
    this.observer?.disconnect();
    this.themeObserver?.disconnect();
    this.chart.dispose();
  }
}
